import { useEffect } from "react";
import { motion } from "framer-motion";
import { CalendarHeart, Flame, PhoneCall, Users } from "lucide-react";
import { IMAGES } from "@/context/AppContext";
import WaysToBegin from "@/components/WaysToBegin";
import { useNoIndex } from "@/hooks/useNoIndex";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}>
    {children}
  </motion.div>
);

const INSIDE = [
  { icon: CalendarHeart, title: "Monthly hangouts", body: "In-person and online gatherings, thoughtfully hosted — the kind of nights you leave feeling more yourself than when you walked in." },
  { icon: Flame, title: "Daily Go & Do challenges", body: "A steady rhythm of small, doable practices that keep faith in motion all week long — not just on Sundays." },
  { icon: PhoneCall, title: "Monthly connect calls", body: "Real conversation with the community — encouragement, honesty, and prayer with people who actually know your name." },
  { icon: Users, title: "First to know, first invited", body: "You hear about everything first and get the first seats. Cancel anytime — no guilt, no games." },
];

export default function OutpouredCommunityPage() {
  useNoIndex();
  useEffect(() => { document.title = "Outpoured Community · Outpoured"; window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="outpoured-community-page" className="bg-[#F4EFE6] text-[#2A2825]">
      {/* HERO */}
      <section className="relative min-h-[78vh] flex items-end grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.circle} alt="Outpoured Community gathered together" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#211E1B] via-[#211E1B]/60 to-[#211E1B]/20" />
        </div>
        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-12 pb-16 md:pb-24 pt-28">
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="tracking-[0.3em] uppercase text-[10px] md:text-xs text-[#E8B486] mb-5">The Club · Monthly</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.28 }}
            className="font-serif-display font-light text-[#F4EFE6] text-4xl sm:text-6xl leading-[1.05] max-w-3xl">
            You were never meant to <span className="italic text-[#E8B486]">grow alone.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.85, delay: 0.42 }}
            className="mt-6 text-[#D9CFC2] font-body text-lg md:text-xl max-w-2xl leading-relaxed">
            A monthly home for people who want the rhythm, the table, and the people — without waiting for the next six-month cohort.
          </motion.p>
        </div>
      </section>

      {/* WHAT IT IS */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-6">What it is</p></Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-8">Connection you can count on, every single month.</h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="space-y-5 text-[#5E4B3C] leading-relaxed font-body text-lg">
              <p>Most of us don't drift away from the life we want because we stopped believing. We drift because we stopped practicing — and because we tried to do it alone.</p>
              <p>Outpoured Community is the fix: a monthly rhythm of hangouts, daily Go &amp; Do challenges, and connect calls that keep you in it. In person and online. Real people, real practice, real momentum.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="py-20 md:py-28 bg-[#EAE3D9]/50 border-y border-[#D8CEBE]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4">What's inside</p></Reveal>
          <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-14 max-w-2xl">Everything you need to keep becoming.</h2></Reveal>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            {INSIDE.map((c, i) => (
              <Reveal key={c.title} delay={0.05 * i}>
                <div className="border-t border-[#D8CEBE] pt-6">
                  <c.icon className="w-6 h-6 text-[#A9724E] mb-4" strokeWidth={1.25} />
                  <h3 className="font-serif-display text-2xl md:text-3xl font-light mb-2">{c.title}</h3>
                  <p className="text-[#5E4B3C] leading-relaxed font-body">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FROM PAST GATHERINGS — photo gallery */}
      <section data-testid="community-gallery" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4">From past gatherings</p></Reveal>
          <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-12 max-w-2xl">This is what showing up looks like.</h2></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Reveal className="col-span-2 md:col-span-2 row-span-2"><img src={IMAGES.reflection3} alt="Friends laughing together at an Outpoured gathering" className="w-full h-full object-cover aspect-[4/5] md:aspect-auto md:h-full" /></Reveal>
            <Reveal delay={0.06} className="col-span-2"><img src={IMAGES.reflectionSinger} alt="Storytelling by candlelight" className="w-full object-cover aspect-[16/10]" /></Reveal>
            <Reveal delay={0.1}><img src={IMAGES.circle} alt="The community gathered in a circle" className="w-full object-cover aspect-square" /></Reveal>
            <Reveal delay={0.14}><img src={IMAGES.reflection2} alt="Women laughing around the table" className="w-full object-cover aspect-square" /></Reveal>
            <Reveal delay={0.18}><img src={IMAGES.reflectionPaint} alt="A live painter working during a gathering" className="w-full object-cover aspect-square" /></Reveal>
            <Reveal delay={0.22}><img src={IMAGES.retreat} alt="A shared meal at an Outpoured gathering" className="w-full object-cover aspect-square" /></Reveal>
          </div>
        </div>
      </section>

      {/* JOIN TIERS */}
      <WaysToBegin />

      {/* CLOSING */}
      <section className="py-24 md:py-32 bg-[#2A2825] text-[#F4EFE6] relative grain">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="tracking-overline text-xs uppercase text-[#E8B486] mb-6">Pull up a chair</p>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl leading-tight mb-6">The table is already set. The only thing missing is you.</h2>
            <p className="text-[#C9BEB0] font-body leading-relaxed max-w-lg mx-auto">Join month to month. Cancel anytime. Show up and watch what a little consistency and community actually does.</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
